import { Link } from '@tanstack/react-router'
import { Bell, CheckCheck, Heart, MessageCircle, Receipt, UserPlus, Wallet } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Kolom } from '@/components/layout/kolom'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/features/notifications/api'
import { formatRelative } from '@/lib/format'
import { cn } from '@/lib/cn'

const severityStyles = {
  info: 'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  critical: 'bg-danger-soft text-danger',
}

export function NotificationsPage() {
  const { data, isLoading } = useNotifications()
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()
  const toast = useToast()

  const notifications = data?.notifications ?? []

  return (
    <Kolom judul="Aktivitas" className="space-y-2">
      <header className="-mx-4 flex flex-wrap items-center justify-between gap-4 border-b border-line px-4 py-3 md:-mx-6 md:px-6">
        <div>
          <p className="text-sm text-subtle">
            {data?.unread_count ? `${data.unread_count} belum dibaca` : 'Semua sudah dibaca'}
          </p>
        </div>

        {data && data.unread_count > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            loading={markAll.isPending}
            onClick={() =>
              markAll.mutate(undefined, {
                onSuccess: () => toast.success('Semua notifikasi ditandai terbaca'),
              })
            }
          >
            <CheckCheck className="size-4" />
            Tandai semua terbaca
          </Button>
        ) : null}
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-2xl" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <EmptyState
          illustration="box"
          icon={<Bell className="size-6" />}
          title="Belum ada aktivitas"
          description="Suka, komentar, pengikut baru, dan kabar pembayaran akan muncul di sini."
        />
      ) : (
        <div>
          <ul className="divide-y divide-[var(--border)]">
            {notifications.map((notification) => {
              const body = (
                <div className="flex gap-4">
                  <span
                    className={cn(
                      'flex size-10 shrink-0 items-center justify-center rounded-xl',
                      severityStyles[notification.severity],
                    )}
                  >
                    <IkonAktivitas jenis={notification.type} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <p className={cn('text-sm', notification.read ? 'font-semibold text-ink' : 'font-bold text-ink')}>
                        {notification.title}
                      </p>
                      <span className="shrink-0 text-xs text-subtle">{formatRelative(notification.created_at)}</span>
                    </div>
                    {notification.body ? (
                      <p className="mt-0.5 text-sm leading-relaxed text-muted">{notification.body}</p>
                    ) : null}
                  </div>

                  {!notification.read ? (
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-brand" aria-label="Belum dibaca" />
                  ) : null}
                </div>
              )

              return (
                <li key={notification.id}>
                  {notification.link ? (
                    <Link
                      to={notification.link}
                      className="-mx-2 block rounded-xl px-2 py-4 transition hover:bg-sunken"
                      onClick={() => {
                        if (!notification.read) markRead.mutate(notification.id)
                      }}
                    >
                      {body}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="-mx-2 block w-[calc(100%+1rem)] rounded-xl px-2 py-4 text-left transition hover:bg-sunken"
                      onClick={() => {
                        if (!notification.read) markRead.mutate(notification.id)
                      }}
                    >
                      {body}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </Kolom>
  )
}

/** Ikon menurut jenis kabar, supaya daftar dapat dipindai tanpa dibaca. */
function IkonAktivitas({ jenis }: { jenis: string }) {
  const kelas = 'size-4.5'
  if (jenis === 'post.liked') return <Heart className={cn(kelas, 'fill-current')} />
  if (jenis === 'post.commented') return <MessageCircle className={kelas} />
  if (jenis === 'user.followed') return <UserPlus className={kelas} />
  if (jenis.startsWith('payout') || jenis.startsWith('wallet')) return <Wallet className={kelas} />
  if (jenis.startsWith('order') || jenis.startsWith('enrollment')) return <Receipt className={kelas} />
  return <Bell className={kelas} />
}
